import 'react-datepicker/dist/react-datepicker.css';

import { TextField } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useRef } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { dateUtils } from '@/utils/dateUtils';

type Props = {
  name: string;
  label: string;
  timezone?: string; // Should always be "America/Los_Angeles" for PDT
  required?: boolean;
  rules?: RegisterOptions;
  onDateChanged?: (oldValue, newValue) => void;
  disabled?: boolean;
  inputFormat?: string; // other formats include 'MM/dd/yyyy hh:mm a' and 'MM/dd/yyyy hh:mm:ss a'
  minDate?: Date | string;
  maxDate?: Date | string;
};

const VNDatePicker = ({
  name,
  label,
  timezone,
  required = false,
  rules,
  onDateChanged = null,
  disabled = false,
  inputFormat = 'MM/dd/yyyy hh:mm a',
  minDate,
  maxDate,
}: Props) => {
  const { control } = useFormContext();
  const inputEl = useRef<HTMLInputElement>(null);

  const parseDateInput = (d: any) => {
    if (!d) return undefined;
    const d1 = DateTime.fromJSDate(d).setZone(timezone);
    return dateUtils.toDate(d1, timezone); // strings
  };

  return (
    <div className="vn-datepicker">
      <Controller
        name={name}
        control={control}
        rules={{
          required: required && 'This is a required field',
          ...rules,
        }}
        render={({ field: { onChange, value, ...field }, fieldState }) => {
          const displayedDate = dateUtils.toJSDate(value, timezone);
          const parsedMinDate = parseDateInput(minDate);
          const parsedMaxDate = parseDateInput(maxDate);
          // console.log('parsedMinDate', { parsedMinDate, parsedMaxDate, displayedDate });

          return (
            <DateTimePicker
              ref={inputEl}
              value={displayedDate}
              disabled={disabled}
              minDateTime={parsedMinDate}
              maxDateTime={parsedMaxDate}
              onChange={(date) => {
                const oldValue = _.cloneDeep(value);
                const newValue = dateUtils.toString(date, timezone);
                // console.log('oldValue, newValue', { oldValue, newValue });
                onChange(newValue);
                if (onDateChanged) {
                  onDateChanged(oldValue, newValue);
                }
              }}
              label={`${label}${required ? ' *' : ''}`}
              inputFormat={inputFormat}
              className="vn-datepicker-input"
              renderInput={(params) => (
                <TextField
                  size="small"
                  value={displayedDate}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  autoComplete="off"
                  {...params}
                  {...field}
                />
              )}
            />
          );
        }}
      />
    </div>
  );
};

export default VNDatePicker;
